// @ts-check
import React from "react";
import PropTypes from "prop-types";

import { TestableCard, TestableText, TestableMetric } from "@components/general/TestableComponents";

/**
 * One KPI in a summary row: a bordered card holding a label and a single large value.
 *
 * The house KPI card - a caller wanting a different look should change this file rather
 * than override at the call site. Two adornment slots because the rows decorate in
 * different places: a delta beside the value, a status dot beside the label.
 *
 * @param {Object} props
 * @param {string} props.testIdPrefix          e.g. "bv-kpi" -> "bv-kpi-at-risk".
 * @param {string} props.testKey               e.g. "at-risk".
 * @param {React.ReactNode} props.label
 * @param {React.ReactNode} props.value
 * @param {React.ReactNode} [props.labelAdornment]  Top right, beside the label.
 * @param {React.ReactNode} [props.valueAdornment]  Bottom right, beside the value.
 * @returns {JSX.Element}
 */
const KpiCard = ({ testIdPrefix, testKey, label, value, labelAdornment, valueAdornment }) => (
  <TestableCard
    dataTestId={`${testIdPrefix}-${testKey}`}
    className="p-4 border border-gray-300 dark:border-gray-700 rounded-xl shadow-none"
  >
    {/* A flex row even with no adornment: one child against justify-between still sits at the start. */}
    <div className="flex items-center justify-between gap-2">
      <TestableText dataTestId={`${testIdPrefix}-label-${testKey}`}>{label}</TestableText>
      {labelAdornment}
    </div>

    <div className="mt-2 flex items-end justify-between">
      <TestableMetric dataTestId={`${testIdPrefix}-value-${testKey}`}>{value}</TestableMetric>
      {valueAdornment}
    </div>
  </TestableCard>
);

KpiCard.propTypes = {
  testIdPrefix: PropTypes.string.isRequired,
  testKey: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  labelAdornment: PropTypes.node,
  valueAdornment: PropTypes.node,
};

export default KpiCard;
